import type { CSSProperties, KeyboardEvent, ReactNode } from 'react'

import Link from 'next/link'
import { useRouter } from 'next/router'
import { ArrowRight } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import { useInView } from '@/hooks/use-in-view'
import { useReducedMotion } from '@/hooks/use-reduced-motion'
import TransitionLink from '@/components/generals/transition-link'
import { DEFAULT_MENU_TAB, MENU, menuHref } from '@/constants/menu'

interface MenuBoardProps {
    tab: string
}

const slugFromUrl = (url: string): string | null => {
    const path = url.split(/[?#]/)[0]

    if (!path.startsWith('/menu')) return null

    const slug = path.replace(/^\/menu\/?/, '').split('/')[0] || DEFAULT_MENU_TAB

    return MENU.some(category => category.slug === slug) ? slug : null
}

const MenuBoard = ({ tab }: MenuBoardProps): ReactNode => {
    const router = useRouter()
    const reduced = useReducedMotion()
    const board = useRef<HTMLElement>(null)
    const scroller = useRef<HTMLDivElement>(null)
    const tabs = useRef<Record<string, HTMLAnchorElement | null>>({})
    const { ref: listRef, inView } = useInView<HTMLDivElement>({ rootMargin: '0px 0px -8% 0px' })
    const [active, setActive] = useState(tab)
    const [shown, setShown] = useState(tab)
    const [fading, setFading] = useState(false)

    useEffect(() => {
        setActive(tab)
    }, [tab])

    useEffect(() => {
        const sync = (url: string) => {
            const slug = slugFromUrl(url)

            if (slug) setActive(slug)
        }

        router.events.on('routeChangeComplete', sync)

        return () => router.events.off('routeChangeComplete', sync)
    }, [router.events])

    useEffect(() => {
        if (active === shown) return

        setFading(true)

        const id = window.setTimeout(() => {
            setShown(active)
            setFading(false)
        }, reduced ? 0 : 300)

        return () => window.clearTimeout(id)
    }, [active, shown, reduced])

    useEffect(() => {
        const container = scroller.current
        const current = tabs.current[active]

        if (!container || !current || container.scrollWidth <= container.clientWidth) return

        container.scrollTo({
            left: current.offsetLeft - (container.clientWidth - current.offsetWidth) / 2,
            behavior: reduced ? 'auto' : 'smooth'
        })
    }, [active, reduced])

    const select = (slug: string) => {
        if (slug === active) return

        setActive(slug)

        const element = board.current

        if (!element) return

        const top = window.scrollY + element.getBoundingClientRect().top - 64

        if (window.scrollY > top + 4) {
            window.scrollTo({ top, behavior: reduced ? 'auto' : 'smooth' })
        }
    }

    const onKeyDown = (event: KeyboardEvent<HTMLAnchorElement>, index: number) => {
        const keys: Record<string, number> = {
            ArrowRight: index + 1,
            ArrowLeft: index - 1,
            Home: 0,
            End: MENU.length - 1
        }

        if (event.key === ' ') {
            event.preventDefault()
            event.currentTarget.click()
            return
        }

        if (!(event.key in keys)) return

        event.preventDefault()

        const next = (keys[event.key] + MENU.length) % MENU.length
        const target = tabs.current[MENU[next].slug]

        target?.focus()
        target?.click()
    }

    const category = MENU.find(item => item.slug === shown) ?? MENU[0]
    const index = MENU.findIndex(item => item.slug === shown)

    return (
        <section ref={board} id='menu' aria-label='Menu' className='relative pb-28 md:pb-40'>
            <div className='sticky top-16 z-20 border-y border-ivory/[0.07] bg-charcoal/85 backdrop-blur-xl'>
                <div
                    ref={scroller}
                    role='tablist'
                    aria-label='Menu sections'
                    className='shell no-scrollbar flex gap-2 overflow-x-auto py-3 md:gap-10 md:py-6'
                >
                    {MENU.map((item, itemIndex) => {
                        const selected = item.slug === active

                        return (
                            <Link
                                key={item.slug}
                                ref={element => {
                                    tabs.current[item.slug] = element
                                }}
                                href={menuHref(item.slug)}
                                replace
                                shallow
                                scroll={false}
                                role='tab'
                                id={`tab-${item.slug}`}
                                aria-selected={selected}
                                aria-controls='menu-panel'
                                tabIndex={selected ? 0 : -1}
                                onClick={() => select(item.slug)}
                                onKeyDown={event => onKeyDown(event, itemIndex)}
                                className={cn(
                                    'caps relative inline-flex h-10 shrink-0 items-center whitespace-nowrap rounded-full border px-4 transition-colors duration-500',
                                    'md:h-auto md:rounded-none md:border-0 md:px-0 md:pb-1 md:font-heading md:text-[1.65rem] md:font-normal md:normal-case md:tracking-[-0.01em]',
                                    selected
                                        ? 'border-brass/70 bg-brass/10 text-brass-bright md:bg-transparent md:italic'
                                        : 'border-ivory/15 text-ivory/65 hover:text-ivory'
                                )}
                            >
                                <span className='mr-2 hidden align-top font-sans text-[0.625rem] tracking-[0.2em] text-ivory/35 md:inline'>
                                    0{itemIndex + 1}
                                </span>

                                {item.label}

                                <span
                                    aria-hidden='true'
                                    className={cn(
                                        'absolute inset-x-0 -bottom-[23px] hidden h-px origin-left bg-brass transition-transform duration-700 ease-film md:block',
                                        selected ? 'scale-x-100' : 'scale-x-0'
                                    )}
                                />
                            </Link>
                        )
                    })}
                </div>
            </div>

            <div className='shell mt-14 grid gap-12 md:mt-20 lg:grid-cols-12 lg:gap-16'>
                <aside
                    className={cn(
                        'lg:col-span-4 transition-opacity duration-300',
                        fading ? 'opacity-0' : 'opacity-100'
                    )}
                >
                    <div className='lg:sticky lg:top-48'>
                        <p className='caps tabular-nums text-ivory/45'>
                            0{index + 1} / 0{MENU.length}
                        </p>

                        <p className='mt-5 font-heading text-[clamp(3.25rem,7vw,6.5rem)] italic leading-[0.9] tracking-[-0.03em]'>
                            {category.label}
                        </p>

                        <p className='caps mt-5 text-brass'>{category.subtitle}</p>

                        <p className='mt-6 max-w-sm text-[0.95rem] leading-relaxed text-ivory/60'>
                            {category.intro}
                        </p>
                    </div>
                </aside>

                <div
                    ref={listRef}
                    id='menu-panel'
                    role='tabpanel'
                    aria-labelledby={`tab-${shown}`}
                    tabIndex={0}
                    className={cn(
                        'outline-none lg:col-span-8 transition-opacity duration-300',
                        fading ? 'opacity-0' : 'opacity-100'
                    )}
                >
                    <h2 className='sr-only'>{category.label}, {category.subtitle}</h2>

                    <ul key={shown} data-list={inView ? 'play' : 'wait'} className='border-t border-ivory/[0.08]'>
                        {category.items.map((item, itemIndex) => (
                            <li
                                key={item.name}
                                className='menu-item border-b border-ivory/[0.08]'
                                style={{ '--i': itemIndex } as CSSProperties}
                            >
                                <div className='group py-6 transition-transform duration-500 ease-film hover:-translate-y-0.5 md:py-7'>
                                    <div className='flex items-baseline'>
                                        <h3 className='font-heading text-[1.4rem] leading-snug tracking-[-0.01em] transition-colors duration-500 group-hover:text-brass-bright md:text-[1.7rem]'>
                                            {item.name}

                                            {item.vegetarian && (
                                                <>
                                                    <span aria-hidden='true' className='ml-3 inline-block size-1.5 -translate-y-[0.3em] rounded-full bg-brass' />
                                                    <span className='sr-only'> (vegetarian)</span>
                                                </>
                                            )}
                                        </h3>

                                        <span aria-hidden='true' className='leader' />

                                        <span className='shrink-0 text-[1.05rem] font-medium tabular-nums text-ivory/90'>
                                            {item.price}
                                        </span>
                                    </div>

                                    <p className='mt-2 max-w-[52ch] text-[0.95rem] leading-relaxed text-ivory/55'>
                                        {item.description}
                                    </p>
                                </div>
                            </li>
                        ))}
                    </ul>

                    {shown === 'sans-alcool' && (
                        <TransitionLink href='/sans-alcool' className='link-line caps mt-10 text-ivory/85'>
                            Visit the bar, with pairings
                            <ArrowRight aria-hidden='true' className='size-4' strokeWidth={1.25} />
                        </TransitionLink>
                    )}

                    <div className='mt-20 flex flex-col gap-10 border-t border-ivory/10 pt-10 md:flex-row md:items-end md:justify-between'>
                        <div>
                            <p className='caps text-brass'>Allergies</p>

                            <p className='mt-4 font-heading text-[clamp(1.8rem,3.2vw,2.6rem)] leading-[1.08] tracking-[-0.015em]'>
                                Tell us about allergies.
                                <em className='block text-ivory/75'>We will guide you.</em>
                            </p>
                        </div>

                        <TransitionLink href='/reserve' className='link-line caps self-start text-ivory md:self-auto'>
                            Reserve a table
                            <ArrowRight aria-hidden='true' className='size-4' strokeWidth={1.25} />
                        </TransitionLink>
                    </div>
                </div>
            </div>
        </section>
    )
}

export default MenuBoard
